"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { MarketingEvent, UserSession } from "@audacia-hq/shared/models";
import { debounce } from "@audacia-hq/shared/utils";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import MarketingApiClient from "../services/marketingAPI";


declare global {
  interface Window {
    clarity: (...args:any) => void;
  }
}



interface EventTrackerContextType {
  addEvent: (event: MarketingEvent) => void;
  userSessionUid?: string | null;
  userSession?: UserSession | null;
}

const EventTrackerContext = createContext<EventTrackerContextType>({
  addEvent: () => ({}),
  userSessionUid: null,
  userSession: null,
});

const SEND_EVENT_DELAY = 2000;

const EventTrackerProvider: React.FC<{children: React.ReactNode; client: MarketingApiClient; userSession: UserSession }> = ({ children, client, userSession }) => {
  const [stackedEvents, setStackedEvents] = useState<any[]>([]);
  const refStackedEvents = useRef<any[]>([]);

  const sendEvents = useCallback(debounce(() => {
    if (!userSession) return;
    client.postEvents(userSession.userSessionUid, refStackedEvents.current);
    setStackedEvents([]);
  }, SEND_EVENT_DELAY), [userSession]);

  const addEvent = (event: MarketingEvent) => setStackedEvents((prevEvents) => ([...prevEvents, event]));

  useEffect(() => {
    refStackedEvents.current = stackedEvents;
    if (!stackedEvents || stackedEvents.length === 0) return;
    sendEvents();
  }, [stackedEvents]);

  useEffect(() => {
    if (!window?.clarity || !userSession) return;
    try {
      userSession.featureFlagVersions?.forEach((ffv) => {
        window?.clarity("set", "feature", `${ffv.identifier}#${ffv.version.key ?? "Control"}`);
      });
    } catch (error) { /* */ }
  }, [userSession]);

  const value = useMemo(() => ({
    addEvent,
    userSession,
  }), [addEvent, userSession]);

  return (
    <EventTrackerContext.Provider value={value}>
      {children}
    </EventTrackerContext.Provider>
  );
};

const useEventTracker = () => useContext(EventTrackerContext);

export default EventTrackerContext;
export { EventTrackerProvider, useEventTracker };
